/**
 * Projection for `AssetsSectionScene` — the Assets section landing.
 *
 * The demo had no standalone Assets landing page: `show('section','assets')`
 * rendered a generic section hero whose tiles were the three division pages
 * (`renderSection`, demo line 2994, fed by `SECTINFO` at line 1994). This
 * transform is that tile list expressed as data, with the counts the hero
 * printed beside each tile derived from the fleet register rather than
 * transcribed.
 *
 * Pure: no React, no fetching. Everything comes from the eager `data/core.ts`
 * and the derivations in `shared/oem-engine.ts`.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/fleet/AssetsSectionScene.tsx
 * @see docs/logic/fleet/assets.md
 */

import { FLEET } from '../../data/core';
import type { ConditionBand, DivisionKey, Machine, Subclass } from '../../schema/machine';
import { ATTENTION_BANDS } from '../../schema/machine';
import { DIVLABEL, DIVNAME, statusOf, SUBS } from '../../shared/oem-engine';

/** The three divisions, in the demo's own order (`SUBS` key order on screen). */
export const DIVISION_ORDER = ['surface', 'drilling', 'loadhaul'] as const satisfies readonly DivisionKey[];

/** One-line description of a division, ported from the Help nav map (demo line 3762 block `assets`). */
export const DIVISION_BLURB: Readonly<Record<DivisionKey, string>> = {
  surface: 'Top-hammer and blasthole drills on the benches.',
  drilling: 'Development jumbos and production longhole rigs.',
  loadhaul: 'Loaders and trucks — diesel, battery-electric and autonomous.',
};

/** One subclass row inside a division card. */
export interface DivisionSubclassRow {
  readonly subclass: Subclass;
  readonly machines: number;
  /** Machines in `poor`, `vpoor` or `major`. */
  readonly toWatch: number;
  /** `/assets/<division>?subclass=<subclass>` */
  readonly to: string;
}

/** One division card on the Assets landing. */
export interface DivisionCard {
  readonly division: DivisionKey;
  /** `DIVLABEL` — what every screen reads. */
  readonly label: string;
  readonly blurb: string;
  readonly machines: number;
  /** Distinct mines the division is present at. */
  readonly mines: number;
  readonly toWatch: number;
  readonly subclasses: readonly DivisionSubclassRow[];
  /** `/assets/<division>` */
  readonly to: string;
}

/** Machines of one division, optionally narrowed to a subclass. */
export const divisionFleet = (division: DivisionKey, subclass?: Subclass | null): Machine[] =>
  FLEET.filter(
    (f) => f.division === DIVNAME[division] && (!subclass || f.subclass === subclass),
  );

/** Machines in an attention band — the demo's `ATTN` filter. */
export const countToWatch = (machines: readonly Machine[]): number =>
  machines.filter((f) => ATTENTION_BANDS.includes(statusOf(f.machine_id))).length;

/** `/assets/<division>` with an optional `?subclass=`, encoded once, here. */
export function divisionRoute(division: DivisionKey, subclass?: Subclass | null): string {
  return subclass
    ? `/assets/${division}?subclass=${encodeURIComponent(subclass)}`
    : `/assets/${division}`;
}

/** The three cards, in {@link DIVISION_ORDER}. */
export function divisionCards(): DivisionCard[] {
  return DIVISION_ORDER.map((division) => {
    const machines = divisionFleet(division);
    return {
      division,
      label: DIVLABEL[division],
      blurb: DIVISION_BLURB[division],
      machines: machines.length,
      mines: new Set(machines.map((f) => f.mine)).size,
      toWatch: countToWatch(machines),
      subclasses: SUBS[division].map((subclass) => {
        const list = machines.filter((f) => f.subclass === subclass);
        return {
          subclass,
          machines: list.length,
          toWatch: countToWatch(list),
          to: divisionRoute(division, subclass),
        };
      }),
      to: divisionRoute(division),
    };
  });
}

/** Fleet-wide band counts, worst first — the strip under the landing header. */
export function assetsBandTally(): { band: ConditionBand; count: number }[] {
  const order: ConditionBand[] = ['major', 'vpoor', 'poor', 'fair', 'good'];
  return order.map((band) => ({
    band,
    count: FLEET.filter((f) => statusOf(f.machine_id) === band).length,
  }));
}
